import { FC, useCallback, useEffect, useMemo } from 'react'

import {
  FormDatePicker,
  FormDropdown,
  FormInput,
  FormTextarea,
  resetAllModifiedDataByKey,
  useGetFormData,
} from 'cng-common/features/form'
import { ValidationType } from 'cng-common/features/form/configs'
import { TFormValidationResult } from 'cng-common/features/form/models'
import { FormValidationBuilder } from 'cng-common/features/form/utils/validation'
import { Translation, useSafetyTranslation } from 'cng-common/features/translations'
import { useAppDispatch } from 'cng-common/hooks/useAppDispatch'
import { Button } from 'core-components/library/button'
import { Static } from 'core-components/types'
import { Crud } from 'core/types/Crud'

import {
  useAddEspActivityMutation,
  useGetEspManagersQuery,
  useGetEspPartnerQuery,
  useUpdateEspActivityMutation,
} from '../../api/espApi'
import {
  ESP_ACTIVITY_TYPE_OPTIONS,
  ESP_MEETING_REASON_OPTIONS,
  getEspActivityTypeByLabel,
  getEspActivityTypeLabel,
  getEspMeetingReasonByLabel,
  getEspMeetingReasonLabel,
  IEspSelectOption,
} from '../../configs/options'
import { ESP_TRANSLATION_KEYS } from '../../configs/translations'
import { ESP_ACTIVITY_FORM_KEY, ESP_QUICK_CHECK_IN_URL } from '../../constants'
import { findEspQuarterActivity, getEspFiscalQuarter } from '../../lib/espFiscalQuarter'
import { IEspActivity } from '../../model/types'

import styles from './EspActivityForm.module.scss'

const KEYS = ESP_TRANSLATION_KEYS.activities
const FORM_KEY = ESP_ACTIVITY_FORM_KEY

interface IEspActivityFormData {
  date: Date
  activityType: string
  meetingReason: string
  outcome: string
  attendees: string
  topics: string
  notesAndDecisions: string
}

interface IEspActivityFormProps {
  partnerId: string
  /** Present => edit mode; absent => add mode. */
  activity?: IEspActivity | null
  /** Called after a successful save so the board can close the panel. */
  onSaved: () => void
  /** Closes the panel without saving. */
  onCancel: () => void
}

// keyField is the caption on purpose: FormDropdown's keyField mechanism poisons a shared
// static Crud with a stale filter — the form stores the displayed label and the submit
// maps it back to the stable option key (getEsp*ByLabel).
const createOptionsCrud = (options: IEspSelectOption[]) =>
  new Crud<IEspSelectOption>({
    fetcher: new Static(options),
    keyField: 'value',
    defaultPaging: { page: 0, pageSize: options.length },
  })

const formatIsoDate = (date: Date): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

/**
 * The "Log activity" panel that sits next to the timeline (wireframe screen 4, right
 * column) — opened from the banner in add mode, or from a timeline entry in edit mode.
 * The board remounts it (React key) on mode/record change, because `useGetFormData` only
 * seeds the store while the form key holds no data yet.
 */
export const EspActivityForm: FC<IEspActivityFormProps> = ({
  partnerId,
  activity,
  onSaved,
  onCancel,
}) => {
  const dispatch = useAppDispatch()
  const [addActivity, { isLoading: isAdding }] = useAddEspActivityMutation()
  const [updateActivity, { isLoading: isUpdating }] = useUpdateEspActivityMutation()

  const t = useSafetyTranslation()
  const isEdit = Boolean(activity)
  const { data: managers } = useGetEspManagersQuery({ partnerId }, { skip: isEdit })
  const { data: espPartner } = useGetEspPartnerQuery({ partnerId })
  const activities = espPartner?.activities

  /* One engagement per quarter, by the date of the meeting; the service keeps no such rule. */
  const validateQuarter = useCallback(
    (value: Date | string) => {
      if (!(value instanceof Date) || !activities) {
        return ''
      }

      return findEspQuarterActivity(activities, value, activity?.id)
        ? t(KEYS.quarterTaken).replace('{quarter}', getEspFiscalQuarter(value).label)
        : ''
    },
    [activities, activity?.id, t]
  )
  const isSubmitting = isAdding || isUpdating

  const initialForm = useMemo<IEspActivityFormData>(
    () => ({
      date: activity ? new Date(activity.date) : new Date(),
      activityType: activity ? getEspActivityTypeLabel(activity.activityType) : '',
      meetingReason: activity ? getEspMeetingReasonLabel(activity.meetingReason) : '',
      outcome: activity?.outcome ?? '',
      attendees: activity?.attendees ?? '',
      topics: activity?.topics ?? '',
      notesAndDecisions: activity?.notesAndDecisions ?? '',
    }),
    [activity]
  )

  const [{ formData }] = useGetFormData<IEspActivityFormData>(FORM_KEY, initialForm)

  const typeCrud = useMemo(() => createOptionsCrud(ESP_ACTIVITY_TYPE_OPTIONS), [])
  const meetingReasonCrud = useMemo(() => createOptionsCrud(ESP_MEETING_REASON_OPTIONS), [])

  const handleSubmit = useCallback(async () => {
    const validation = FormValidationBuilder.build(FORM_KEY)
    const errors = await Promise.all([
      validation.call('date', formData.date),
      validation.call('activityType', formData.activityType),
      validation.call('meetingReason', formData.meetingReason),
      validation.call('outcome', formData.outcome),
    ])

    if (errors.some((error): error is TFormValidationResult => Boolean(error))) {
      return
    }

    // The dropdowns store displayed labels — resolve them back to the stable keys.
    const activityType = getEspActivityTypeByLabel(formData.activityType)
    const meetingReason = getEspMeetingReasonByLabel(formData.meetingReason)
    const outcome = formData.outcome.trim()

    if (!activityType || !meetingReason || !outcome) {
      return
    }

    const input = {
      partnerId,
      date: formatIsoDate(formData.date),
      activityType,
      meetingReason,
      outcome,
      attendees: formData.attendees,
      topics: formData.topics,
      notesAndDecisions: formData.notesAndDecisions,
    }

    try {
      if (activity) {
        await updateActivity({ activityId: activity.id, input }).unwrap()
      } else {
        await addActivity(input).unwrap()
      }

      onSaved()
    } catch {
      // the api layer already surfaced the error toast; keep the form open
    }
  }, [activity, addActivity, formData, onSaved, partnerId, updateActivity])

  useEffect(() => {
    return () => {
      dispatch(resetAllModifiedDataByKey(FORM_KEY))
    }
  }, [dispatch])

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <span className={styles.title}>
          <Translation text={isEdit ? KEYS.formEditTitle : KEYS.formAddTitle} span />
        </span>
      </div>

      <div className={styles.two}>
        <FormDatePicker
          isRequired
          formKey={FORM_KEY}
          field="date"
          defaultValue={initialForm.date}
          label={KEYS.dateLabel}
          onValidate={validateQuarter}
          validationType={ValidationType.ONCHANGE}
        />

        <FormDropdown<IEspSelectOption>
          isStatic
          isRequired
          formKey={FORM_KEY}
          field="activityType"
          defaultValue={initialForm.activityType}
          source={typeCrud}
          caption="value"
          label={KEYS.typeLabel}
        />
      </div>

      <FormDropdown<IEspSelectOption>
        isStatic
        isRequired
        formKey={FORM_KEY}
        field="meetingReason"
        defaultValue={initialForm.meetingReason}
        source={meetingReasonCrud}
        caption="value"
        label={KEYS.meetingReasonLabel}
      />

      <FormInput
        formKey={FORM_KEY}
        field="attendees"
        defaultValue={initialForm.attendees}
        type="text"
        label={KEYS.attendeesLabel}
      />

      <FormInput
        formKey={FORM_KEY}
        field="topics"
        defaultValue={initialForm.topics}
        type="text"
        label={KEYS.topicsLabel}
      />

      <FormTextarea
        isRequired
        formKey={FORM_KEY}
        field="outcome"
        defaultValue={initialForm.outcome}
        label={KEYS.outcomeLabel}
        rows={3}
      />

      <div className={styles.field}>
        <FormTextarea
          formKey={FORM_KEY}
          field="notesAndDecisions"
          defaultValue={initialForm.notesAndDecisions}
          label={KEYS.notesLabel}
          rows={4}
        />
        <p className={styles.hint}>
          <Translation text={KEYS.notesHintBefore} span />{' '}
          <a
            className={styles.hintLink}
            href={ESP_QUICK_CHECK_IN_URL}
            target="_blank"
            rel="noreferrer"
          >
            <Translation text={KEYS.notesHintLink} span />
          </a>{' '}
          <Translation text={KEYS.notesHintAfter} span />
        </p>
      </div>

      {!isEdit && managers?.length ? (
        <p className={styles.hint}>
          <Translation
            text={KEYS.managersCopy}
            replaceParams={[{ key: '{managers}', value: managers.join(', ') }]}
            span
          />
        </p>
      ) : null}

      <div className={styles.footer}>
        <Button variant="text" color="grey" onClick={onCancel} disabled={isSubmitting}>
          <Translation text={KEYS.cancel} />
        </Button>

        <Button
          color="primary"
          variant="contained"
          onClick={handleSubmit}
          loading={isSubmitting}
          disabled={isSubmitting}
        >
          <Translation text={KEYS.saveActivity} />
        </Button>
      </div>
    </div>
  )
}

EspActivityForm.displayName = 'EspActivityForm'
